import { inject, Injectable, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

import { MODULE_ROUTE, SECRET_ROUTE } from '@core/models/routes.model';

const STORAGE_KEY = 'tk_recent_secrets';
const MAX_RECENT = 10;

// Matches /secrets/<id> — excludes known sub-routes (trash, recent, folders, ...)
const EXCLUDED_SEGMENTS = new Set<string>(Object.values(SECRET_ROUTE));
const SECRET_DETAIL_PATTERN = new RegExp(`^${MODULE_ROUTE.SECRETS}/([^/]+)$`);

/**
 * Forget the recently opened secrets stored in this browser. `Auth0Service` calls it on logout and
 * on a start without a session, so the next user of the browser is not shown someone else's list.
 */
export function forgetRecentSecrets(): void {
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch {
        // storage unavailable: nothing to forget
    }
}

@Injectable({ providedIn: 'root' })
export class RecentSecretsService {
    private readonly router = inject(Router);
    private readonly recentIds = signal<string[]>(this.loadFromStorage());

    constructor() {
        this.router.events.pipe(
            filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        ).subscribe(event => {
            const urlWithoutQuery = event.urlAfterRedirects.split('?')[0];
            const match = SECRET_DETAIL_PATTERN.exec(urlWithoutQuery);
            if (match && !EXCLUDED_SEGMENTS.has(match[1])) {
                this.track(match[1]);
            }
        });
    }

    public getRecentIds(): string[] {
        return this.recentIds();
    }

    private track(secretId: string): void {
        const current = this.recentIds().filter(id => id !== secretId);
        const updated = [secretId, ...current].slice(0, MAX_RECENT);
        this.recentIds.set(updated);
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
        } catch {
            // storage unavailable: the list lives in this tab only
        }
    }

    private loadFromStorage(): string[] {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            return stored ? JSON.parse(stored) as string[] : [];
        } catch {
            return [];
        }
    }
}
